import * as Sentry from '@sentry/react';
import { ExtraErrorData, CaptureConsole } from '@sentry/integrations';
import { Event } from '@sentry/types';

/**
 * We want to log to Sentry:
 * - console.error messages
 * - Any other event that's not a console.log/info/warning
 */
function filterOutEvents(event: Event): Event | null {
  // Log everything that's not a console event. This is the default
  if (event.logger !== 'console') {
    return event;
  }

  // Log only console errors.
  if (event.level === 'error') {
    return event;
  }

  // Sentry will ignore this event
  return null;
}

export const initSentry = () => {
  return Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    release: 'unknown-blog-release',
    integrations: [
      // Extracts all non-native attributes from the Error object and attaches
      // them to the event as the `extra` data.
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/plugin/#extraerrordata
      new ExtraErrorData(),
      // Captures all Console API calls and redirects them to Sentry using the captureMessage call.
      // It then retriggers to preserve default native behavior.
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/plugin/#captureconsole
      new CaptureConsole(),
    ],
    beforeSend(event: Event) {
      const filteredEvent = filterOutEvents(event);

      /**
       * We're not interested in this event.
       */
      if (!filteredEvent) {
        return null;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.info('Sentry Would have captured exception', filteredEvent);

        return null;
      }

      return filteredEvent;
    },
    /**
     * Avoid reporting errors from any of these domains.
     * https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/#decluttering-sentry
     */
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  });
};
